import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Circle,
  Clock3,
  FileText,
  UserRound,
  Target,
  Archive,
  XCircle,
  Upload,
  ExternalLink,
  File,
  Loader2,
  Trash2,
} from "lucide-react";
import api from "../../lib/api";
import { eur, pct } from "../../lib/format";

const ETAPES = [
  { key: "creation", label: "Création", field: "date_creation" },
  { key: "envoi", label: "Envoyé", field: "date_envoi" },
  { key: "acceptation", label: "Accepté", field: "date_acceptation" },
  { key: "commande", label: "Commandé", field: "date_commande" },
  { key: "livraison", label: "Livré", field: "date_livraison" },
  { key: "facturation", label: "Facturé", field: "date_facturation" },
  { key: "encaissement", label: "Encaissé", field: "date_encaissement" },
];

function formatDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

function formatDateTime(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

export default function Dossier360({ devis, canSeeMargins }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentType, setDocumentType] = useState("Autres");
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const [documentSuccess, setDocumentSuccess] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [deletingDocumentId, setDeletingDocumentId] = useState("");

  useEffect(() => {
    if (!devis?.id) {
      setTimeline([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/devis/${devis.id}/timeline`);
        if (!cancelled) setTimeline(data?.timeline || []);
      } catch {
        if (!cancelled) setTimeline([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [devis?.id]);

  const loadDocuments = async () => {
    if (!devis?.dossier_id) {
      setDocuments([]);
      return;
    }

    setDocumentsLoading(true);
    setDocumentError("");

    try {
      const { data } = await api.get(
        `/dossiers/${devis.dossier_id}/documents`
      );

      setDocuments(Array.isArray(data) ? data : []);
    } catch (error) {
      setDocuments([]);
      setDocumentError(
        error?.response?.data?.detail ||
        "Impossible de charger les documents du dossier."
      );
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [devis?.dossier_id]);

  const handleDocumentUpload = async (event) => {
    const file = event.target.files?.[0];

    if (!file || !devis?.dossier_id) return;

    setDocumentError("");
    setDocumentSuccess("");
    setUploadingDocument(true);
    setSelectedFileName(file.name);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", documentType);

      await api.post(
        `/dossiers/${devis.dossier_id}/documents/upload`,
        formData
      );

      setDocumentSuccess(`Document "${file.name}" ajouté.`);
      await loadDocuments();
    } catch (error) {
      setDocumentError(
        error?.response?.data?.detail ||
        "Impossible d'envoyer le document."
      );
    } finally {
      setUploadingDocument(false);
      event.target.value = "";
    }
  };

  const handleDocumentDelete = async (document) => {
    if (!document?.id || deletingDocumentId) return;

    const confirmed = window.confirm(
      `Supprimer le document "${document.nom}" ?\n\n` +
      `Le fichier Google Drive sera placé dans la corbeille.`
    );

    if (!confirmed) return;

    setDeletingDocumentId(document.id);
    setDocumentError("");
    setDocumentSuccess("");

    try {
      await api.delete(`/documents/${document.id}`);

      setDocuments((current) =>
        current.filter((item) => item.id !== document.id)
      );

      setDocumentSuccess(`Document "${document.nom}" supprimé.`);
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        "Impossible de supprimer le document.";

      setDocumentError(message);
    } finally {
      setDeletingDocumentId("");
    }
  };

  const formatFileSize = (bytes) => {
    const size = Number(bytes || 0);

    if (!size) return "Taille inconnue";
    if (size < 1024) return `${size} o`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} Ko`;

    return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const progression = useMemo(() => {
    let lastDone = -1;

    ETAPES.forEach((etape, index) => {
      if (devis?.[etape.field]) lastDone = index;
    });

    return ETAPES.map((etape, index) => ({
      ...etape,
      done: Boolean(devis?.[etape.field]),
      current: index === lastDone + 1,
    }));
  }, [devis]);

  if (!devis) return null;

  const archive = Boolean(devis.archivee || devis.statut === "Clôturé");
  const ht = Number(devis.total_ht ?? devis.montant_total ?? 0) || 0;
  const cout = Number(devis.total_cout_ht ?? devis.cout_produits ?? 0) || 0;
  const marge = Number(
    devis.marge_brute_ht ??
    devis.calculated_margin ??
    devis.historical_margin ??
    (ht - cout)
  ) || 0;
  const taux = ht ? (marge / ht) * 100 : 0;

  const commercial =
    devis.commercial_responsable ||
    devis.commercial ||
    "Non attribué";

  const latestAction = timeline.find((item) => item.type === "action");
  const nextAction = timeline.find(
    (item) => item.type === "tache" && item.statut !== "terminee"
  );

  return (
    <div className="space-y-4">
      {/* Identité */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 mb-2">
            <UserRound className="h-4 w-4" />
            Client
          </div>
          <div className="font-semibold text-slate-800">
            {devis.client_nom || "Client non renseigné"}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {devis.chantier_nom || "Pas de chantier renseigné"}
          </div>
        </div>

        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 mb-2">
            <Target className="h-4 w-4" />
            Commercial
          </div>
          <div className="font-semibold text-slate-800">
            {commercial}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {devis.famille || "Famille non renseignée"}
          </div>
        </div>

        <div className={`rounded-xl border p-4 ${
          archive
            ? "bg-slate-50 border-slate-300"
            : "bg-white border-slate-200"
        }`}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 mb-2">
            <Archive className="h-4 w-4" />
            État du dossier
          </div>
          <div className={`font-semibold ${
            devis.statut === "Refusé"
              ? "text-red-700"
              : "text-slate-800"
          }`}>
            {devis.statut === "Refusé"
              ? "Dossier refusé"
              : archive
                ? "Archivé"
                : devis.statut || "Brouillon"}
          </div>

          {devis.statut === "Refusé" && (
            <div className="text-xs text-red-600 mt-1">
              Parcours terminé
              {devis.date_refus ? ` · ${formatDate(devis.date_refus)}` : ""}
              {devis.refuse_par ? ` · ${devis.refuse_par}` : ""}
            </div>
          )}

          {archive && devis.statut !== "Refusé" && (
            <div className="text-xs text-slate-500 mt-1">
              Consultation uniquement
            </div>
          )}
        </div>
      </div>

      {devis.statut === "Refusé" && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600">
              <XCircle className="h-5 w-5" />
            </div>

            <div>
              <div className="font-semibold text-red-800">
                Dossier refusé — parcours terminé
              </div>

              <p className="text-sm text-red-700 mt-1">
                Le dossier a été refusé et archivé automatiquement.
                Il n'est plus actif dans le suivi commercial.
              </p>

              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-red-600">
                {devis.date_refus && (
                  <span>
                    Refusé le {formatDate(devis.date_refus)}
                  </span>
                )}

                {devis.refuse_par && (
                  <span>
                    par {devis.refuse_par}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Parcours métier */}
      <div className="rounded-xl bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-semibold text-slate-800">Parcours du dossier</h3>
            <p className="text-xs text-slate-400 mt-1">
              Les dates proviennent des transitions métier enregistrées.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {progression.map((etape) => (
            <div key={etape.key} className="relative">
              <div className={`rounded-lg p-3 border h-full ${
                etape.done
                  ? "bg-emerald-50 border-emerald-200"
                  : "bg-slate-50 border-slate-200"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {etape.done ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Circle className="h-4 w-4 text-slate-300" />
                  )}
                  <span className="text-xs font-semibold text-slate-700">
                    {etape.label}
                  </span>
                </div>
                <div className="text-[11px] text-slate-400">
                  {etape.done
                    ? formatDate(devis[etape.field])
                    : "À venir"}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rentabilité */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric label="CA HT" value={eur(ht)} />
        {canSeeMargins && (
          <>
            <Metric label="Coût HT" value={eur(cout)} />
            <Metric label="Marge HT" value={eur(marge)} success />
            <Metric label="Taux de marge" value={pct(taux)} success />
          </>
        )}
      </div>

      {/* Activité */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 mb-3">
            <Clock3 className="h-4 w-4" />
            Dernière activité
          </div>

          {latestAction ? (
            <>
              <div className="font-semibold text-slate-800">
                {latestAction.label || "Action commerciale"}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {formatDateTime(latestAction.date)}
                {latestAction.auteur ? ` · ${latestAction.auteur}` : ""}
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-400">
              Aucune action commerciale enregistrée.
            </div>
          )}
        </div>

        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 mb-3">
            <Target className="h-4 w-4" />
            Prochaine action
          </div>

          {nextAction ? (
            <>
              <div className="font-semibold text-slate-800">
                {nextAction.label || "Action à effectuer"}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {formatDateTime(nextAction.date)}
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-400">
              Aucune action planifiée.
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="rounded-xl bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-800">Historique du dossier</h3>
            <p className="text-xs text-slate-400 mt-1">
              Journal des événements et actions commerciales.
            </p>
          </div>
          <FileText className="h-5 w-5 text-slate-300" />
        </div>

        {loading ? (
          <div className="text-sm text-slate-400 py-4">
            Chargement de l'historique…
          </div>
        ) : timeline.length === 0 ? (
          <div className="text-sm text-slate-400 py-4">
            Aucun événement détaillé enregistré.
          </div>
        ) : (
          <div className="space-y-3">
            {timeline.map((item, index) => (
              <div key={`${item.id || "event"}-${index}`} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="h-2.5 w-2.5 rounded-full bg-blue-500 mt-1.5" />
                  {index < timeline.length - 1 && (
                    <div className="w-px flex-1 bg-slate-200 mt-1" />
                  )}
                </div>

                <div className="pb-3 min-w-0">
                  <div className="font-medium text-sm text-slate-800">
                    {item.label || "Événement"}
                  </div>
                  <div className="text-xs text-slate-400">
                    {formatDateTime(item.date)}
                    {item.auteur ? ` · ${item.auteur}` : ""}
                  </div>
                  {item.commentaire && (
                    <div className="text-xs text-slate-500 mt-1">
                      {item.commentaire}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-600" />
              <div className="font-semibold text-slate-800">
                Documents du dossier
              </div>

              {devis.dossier_id && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                  {documents.length}
                </span>
              )}
            </div>

            {devis.dossier_id ? (
              <div className="mt-1 text-xs text-slate-500">
                Pièces associées au dossier commercial.
              </div>
            ) : (
              <div className="mt-1 text-xs text-amber-600">
                Ce devis n'est pas rattaché à un dossier.
              </div>
            )}
          </div>

          {devis.dossier_id && (
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                disabled={uploadingDocument}
                className="h-9 rounded-md border border-slate-300 bg-white px-3 text-xs text-slate-700 outline-none focus:border-slate-500"
              >
                <option value="Plans">Plans</option>
                <option value="CCTP">CCTP</option>
                <option value="Photos">Photos</option>
                <option value="Devis">Devis</option>
                <option value="Autres">Autres</option>
              </select>

              <label
                className={[
                  "inline-flex h-9 items-center gap-2 rounded-md px-3 text-xs font-medium transition",
                  uploadingDocument
                    ? "cursor-not-allowed bg-slate-100 text-slate-400"
                    : "cursor-pointer bg-slate-900 text-white hover:bg-slate-800",
                ].join(" ")}
              >
                {uploadingDocument ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}

                {uploadingDocument
                  ? "Envoi..."
                  : "Ajouter un document"}

                <input
                  type="file"
                  className="hidden"
                  disabled={uploadingDocument}
                  onChange={handleDocumentUpload}
                />
              </label>
            </div>
          )}
        </div>

        {selectedFileName && uploadingDocument && (
          <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
            Envoi de{" "}
            <span className="font-medium text-slate-700">
              {selectedFileName}
            </span>
            …
          </div>
        )}

        {documentSuccess && (
          <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            {documentSuccess}
          </div>
        )}

        {documentError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {documentError}
          </div>
        )}

        {!devis.dossier_id ? (
          <div className="mt-5 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            Les documents seront disponibles dès que ce devis sera
            rattaché à un dossier.
          </div>
        ) : documentsLoading ? (
          <div className="mt-5 flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement des documents…
          </div>
        ) : documents.length === 0 ? (
          <div className="mt-5 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
            <File className="mx-auto h-7 w-7 text-slate-300" />
            <div className="mt-2 text-sm font-medium text-slate-600">
              Aucun document
            </div>
            <div className="mt-1 text-xs text-slate-400">
              Ajoutez les plans, CCTP, photos ou autres pièces du dossier.
            </div>
          </div>
        ) : (
          <div className="mt-5 overflow-hidden rounded-lg border border-slate-200">
            <div className="hidden grid-cols-[minmax(0,1fr)_100px_90px_150px] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:grid">
              <div>Document</div>
              <div>Type</div>
              <div>Version</div>
              <div>Stockage</div>
            </div>

            <div className="divide-y divide-slate-100">
              {documents.map((document) => {
                const isDrive =
                  document.storage_provider === "google_drive";

                const isPending =
                  document.storage_provider === "pending";

                return (
                  <div
                    key={document.id}
                    className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_100px_90px_150px] sm:items-center"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 shrink-0 text-slate-400" />

                        <div className="truncate text-sm font-medium text-slate-700">
                          {document.nom}
                        </div>
                      </div>

                      <div className="mt-1 pl-6 text-[11px] text-slate-400">
                        {formatFileSize(document.taille)}

                        {document.date_creation
                          ? ` · ${formatDateTime(document.date_creation)}`
                          : ""}
                      </div>
                    </div>

                    <div>
                      <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-600">
                        {document.type || "Autres"}
                      </span>
                    </div>

                    <div className="text-xs text-slate-500">
                      V{document.version || 1}
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      {isDrive ? (
                        <>
                          <span className="inline-flex rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-medium text-emerald-700">
                            Google Drive
                          </span>

                          <div className="flex items-center gap-1">
                            {document.storage_url && (
                              <a
                                href={document.storage_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                title="Ouvrir dans Google Drive"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            )}

                            <button
                              type="button"
                              onClick={() => handleDocumentDelete(document)}
                              disabled={deletingDocumentId === document.id}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                              title="Supprimer le document"
                            >
                              {deletingDocumentId === document.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </>
                      ) : isPending ? (
                        <>
                          <span className="inline-flex rounded-full bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-700">
                            En attente
                          </span>

                          <button
                            type="button"
                            onClick={() => handleDocumentDelete(document)}
                            disabled={deletingDocumentId === document.id}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                            title="Supprimer le document"
                          >
                            {deletingDocumentId === document.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-500">
                            {document.storage_provider || "Inconnu"}
                          </span>

                          <button
                            type="button"
                            onClick={() => handleDocumentDelete(document)}
                            disabled={deletingDocumentId === document.id}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                            title="Supprimer le document"
                          >
                            {deletingDocumentId === document.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-3 text-[10px] text-slate-400">
          Taille maximale : 50 Mo par document.
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, success }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className={`font-mono text-lg font-bold mt-1 ${
        success ? "text-emerald-600" : "text-slate-800"
      }`}>
        {value}
      </div>
    </div>
  );
}
